import { useEffect, RefObject } from 'react';
import { GridApi } from 'ag-grid-community';

/**
 * Clears the cell selection and focused cell whenever the user clicks outside
 * the grid. AG Grid does not do this on its own, so a cell range stays
 * highlighted even after focus has clearly moved away from the table.
 *
 * Popups owned by the grid (column menus, context menus, tooltips) are hosted
 * outside the container, in the top layer, so they count as inside the table
 * too.
 */
export const useClearCellSelectionOnOutsideClick = (
  containerRef: RefObject<HTMLElement | null>,
  apiRef: RefObject<GridApi | null>,
  popupHost: HTMLElement
) => {
  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      const api = apiRef.current;
      const container = containerRef.current;
      if (!api || !container) {
        return;
      }

      const target = event.target;
      if (
        !(target instanceof Node) ||
        container.contains(target) ||
        popupHost.contains(target)
      ) {
        return;
      }

      api.clearCellSelection();
      api.clearFocusedCell();
    };

    document.addEventListener('mousedown', handleMouseDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [apiRef, containerRef, popupHost]);
};
